import React, { Fragment, useMemo } from 'react'

import {
  Box,
  Card,
  CardContent,
  CardStrip,
  Loader,
  SimpleTable,
  Typography,
} from '@percept/mui'

import { DimensionalSummary } from 'components/DimensionalSummary'

import { PerformanceTableProps } from 'components/Tables'

import { DownloadTrigger } from './DownloadTrigger'

import { usePerformanceTotalsTableStyles } from '../styles'

import { useOrgUnitPotentialEfficiencyScores } from '@percept/hooks'

import { useWastageProviderComparisons } from './comparisonHook'

import { useTargetCurrency } from 'hooks'

import { some, uniqBy } from 'lodash-es'

import { dateFnsOps, isoDateUTC } from '@percept/utils'

import {
  cellRenderers,
  rangedValueCellRenderers,
} from './lib'

import {
  makeComparisonTableRows,
  usePotentialEfficiencyDatasets,
  useRangedPotentialEfficiencyTableData,
} from './datasets'

import { PerformanceReportingDatum } from 'types'

import {
  PlatformUnitParams,
  ProviderPotentialEfficiencyScores,
  ReportProviderV2,
} from '@percept/types'



export const PotentialEfficiencies = ({
  org_unit_id,
  referenceDate,
  suppressLoader = false,
}: PlatformUnitParams & {
  referenceDate: Date | null
  suppressLoader?: boolean
}): JSX.Element => {

  const [target_currency] = useTargetCurrency()

  const today = new Date()
  const oneYearAgo = dateFnsOps.subYears(today, 1)

  const cacheTime = 1000 * 60 * 60 * 24

  const currentYearEfficiencies = useOrgUnitPotentialEfficiencyScores({
    org_unit_id,
    currency: target_currency,
    reference_date: isoDateUTC(today),
    start_date: isoDateUTC(dateFnsOps.addDays(oneYearAgo, 1)),
    cacheTime,
  })

  const previousYearEfficiencies = useOrgUnitPotentialEfficiencyScores({
    org_unit_id,
    currency: target_currency,
    reference_date: isoDateUTC(oneYearAgo),
    start_date: isoDateUTC(dateFnsOps.subYears(oneYearAgo, 1)),
    cacheTime,
  })

  const combinedEffiencies = useMemo(() => {
    if( !currentYearEfficiencies.data || !previousYearEfficiencies.data ){
      return null
    }
    const combinedResponses = [...previousYearEfficiencies.data, ...currentYearEfficiencies.data]
    const byProvider: Record<ReportProviderV2, ProviderPotentialEfficiencyScores> = combinedResponses.reduce( (acc, response) => {
      const provider = response.source_provider.slug
      if( acc[provider] ){
        acc[provider].potential_efficiency_scores = uniqBy(
          acc[provider].potential_efficiency_scores.concat(response.potential_efficiency_scores),
          'start'
        )
      }else{
        acc[provider] = response
      }
      return acc
    }, {} as Record<ReportProviderV2, ProviderPotentialEfficiencyScores>)
    return Object.values(byProvider)
  }, [currentYearEfficiencies.data, previousYearEfficiencies.data])

  const datasets = usePotentialEfficiencyDatasets({
    potentialEfficiencyScores: combinedEffiencies,
    currency: target_currency,
  })

  if( process.env.APP === 'vodafone' && referenceDate ){
    // Ugly temporary hack to make the reference date behave like it used to, showing
    // the latest completed report date. Really need to make this all endpoint-driven.
    referenceDate = dateFnsOps.subDays(dateFnsOps.startOfWeek(referenceDate, { weekStartsOn: 1 }), 1)
  }

  const wastageProviderComparisons = useWastageProviderComparisons({ org_unit_id, referenceDate })

  const { rows, columns } = useRangedPotentialEfficiencyTableData({ datasets })

  const tableClasses = usePerformanceTotalsTableStyles()

  if( !datasets || !currentYearEfficiencies.data || some(Object.values(wastageProviderComparisons), q => q.loading ) ){
    if( suppressLoader ){
      return <Box height='12rem' />
    }else{
      return <Loader height='12rem' preset='centered' />
    }
  }

  return (
    <Fragment>
      <DimensionalSummary
        dimension='cost'
        labelHistory={
          <Fragment>
            History
            <Box display='inline-flex' ml={2}>
              <DownloadTrigger
                org_unit_id={org_unit_id}
                currency={target_currency}
                datasets={datasets}
                color='secondary' />
            </Box>
          </Fragment>
        }
        labelChange='Comparison'
        labelSplit='Breakdown'
        referenceDate={referenceDate}
        axisChartMinDate={oneYearAgo}
        datasets={datasets}
        currency={target_currency}
        color='neutral'
        PerformanceTableProps={{
          rows: makeComparisonTableRows(wastageProviderComparisons),
          showNowColumn: false,
          renderers: cellRenderers as PerformanceTableProps<PerformanceReportingDatum>['renderers'],
          showQuarterlyFigure: true,
        }} />

      <Box my={3} />

      <Box mb={3}>
        <Typography variant='h5'>
          Totals
        </Typography>
      </Box>

      <Card>
        {/* <CardStrip color='neutral' /> */}
        <CardContent>
          <SimpleTable
            classes={tableClasses}
            columns={columns}
            rows={rows}
            renderers={rangedValueCellRenderers}
            sortable />
        </CardContent>
      </Card>
    </Fragment>
  )
}
